import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { Modal } from '@/components/ui/Modal';
import { UserAvatar } from '@/components/ui/Avatar';
import PostComposer from '@/components/feed/PostComposer';

export interface QuoteTarget {
  id: string;
  content: string | null;
  author: string;
  avatarUrl: string | null;
}

interface QuoteModalProps {
  target: QuoteTarget | null;
  onClose: () => void;
  onQuoted?: () => void;
}

/**
 * Shared quote composer used by feed cards and thread replies. Holding the
 * repost button opens this with the target post already attached.
 */
export default function QuoteModal({ target, onClose, onQuoted }: QuoteModalProps) {
  if (!target) return null;

  return createPortal(
    <Modal isOpen onClose={onClose}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <UserAvatar src={target.avatarUrl} size="sm" className="shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              {target.author}
            </p>
            <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
              {target.content || 'Media post'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
            aria-label="Close quote composer"
          >
            <X size={16} />
          </button>
        </div>
        <PostComposer
          quotedPostId={target.id}
          onPostCreated={() => {
            onQuoted?.();
            onClose();
          }}
        />
      </div>
    </Modal>,
    document.body,
  );
}
